import React, { useEffect, useRef, useCallback } from "react";

/**
 * LyricsDisplay — renders static or timed lyrics.
 *
 * Props:
 *  - mode: "static" | "timed"
 *  - staticLines: string[]           (parsed from markdown)
 *  - timedLines: { time, text }[]    (parsed from LRC)
 *  - currentTime: number             (seconds, from <audio>)
 *  - onSeek: (timeInSeconds) => void (clicking a timed line seeks audio)
 */
const LyricsDisplay = ({ mode, staticLines, timedLines, currentTime, onSeek }) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);
  const lastActiveIndex = useRef(-1);

  // ── Find active timed-lyric index via binary search ──────
  const getActiveIndex = useCallback((time) => {
    if (!timedLines || timedLines.length === 0) return -1;
    let lo = 0;
    let hi = timedLines.length - 1;
    let result = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (timedLines[mid].time <= time) {
        result = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return result;
  }, [timedLines]);

  // ── Auto-scroll to active line ──────────────────────────
  useEffect(() => {
    if (mode !== "timed") return;
    const idx = getActiveIndex(currentTime);
    if (idx !== lastActiveIndex.current && activeRef.current) {
      lastActiveIndex.current = idx;
      activeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentTime, mode, getActiveIndex]);

  // ── Static lyrics view ──────────────────────────────────
  if (mode === "static") {
    if (!staticLines || staticLines.length === 0) {
      return (
        <div className="lyrics-panel">
          <div className="no-lyrics">No lyrics available</div>
        </div>
      );
    }
    return (
      <div className="lyrics-panel static-lyrics" ref={containerRef}>
        <div className="lyrics-panel-header">Lyrics</div>
        {staticLines.map((line, i) => {
          if (line === "") {
            return <div key={i} className="lyrics-blank" />;
          }
          // Detect section labels like [00:07] or [01:05] Verse 1
          const sectionMatch = line.match(/^\[[\d:]+\]\s*(.*)/);
          if (sectionMatch && sectionMatch[1] && /^(verse|chorus|bridge|outro|intro|nakarat|hook|x\d)/i.test(sectionMatch[1].trim())) {
            return (
              <div key={i} className="lyrics-section-label">
                {sectionMatch[1].trim()}
              </div>
            );
          }
          // Strip leading timestamp markers from display
          const cleanLine = line.replace(/^\[[\d:]+\]\s*/, "");
          if (!cleanLine.trim()) return <div key={i} className="lyrics-blank" />;
          return (
            <div key={i} className="lyrics-line">
              {cleanLine}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Timed lyrics view ───────────────────────────────────
  if (!timedLines || timedLines.length === 0) {
    return (
      <div className="lyrics-panel">
        <div className="no-lyrics">No timed lyrics available</div>
      </div>
    );
  }

  const activeIndex = getActiveIndex(currentTime);

  return (
    <div className="lyrics-panel timed-lyrics" ref={containerRef}>
      <div className="lyrics-spacer" />
      {timedLines.map((line, i) => {
        const isActive = i === activeIndex;
        const isPast = i < activeIndex;
        return (
          <div
            key={i}
            ref={isActive ? activeRef : null}
            className={
              "timed-line" +
              (isActive ? " active" : "") +
              (isPast ? " past" : "")
            }
            onClick={() => onSeek && onSeek(line.time)}
          >
            {line.text}
          </div>
        );
      })}
      <div className="lyrics-spacer" />
    </div>
  );
};

export default LyricsDisplay;
